import { searchRegexLanguageParser } from '../parser';
import { SearchQueryTerm, SearchQueryTermWithoutPositions } from '../types';

import {
  areTermsSameType,
  CursorMatch,
  findCursorMatch,
  getInsertionIndex,
  isCriterion,
  isGroupEnd,
  isGroupStart,
  isNotOperator,
  isOperator,
} from './utils';

const replaceTermAtIndex = (
  terms: SearchQueryTermWithoutPositions[],
  index: number,
  term: SearchQueryTermWithoutPositions,
) => {
  const next = [...terms];
  next[index] = term;
  return next;
};

const insertTermAtIndex = (
  terms: SearchQueryTermWithoutPositions[],
  index: number,
  term: SearchQueryTermWithoutPositions,
) => {
  return [...terms.slice(0, index), term, ...terms.slice(index)];
};

const insertNotTerm = (
  terms: SearchQueryTerm[],
  cursorPosition: number,
  cursorMatch: CursorMatch | undefined,
  newTerm: SearchQueryTermWithoutPositions,
): SearchQueryTermWithoutPositions[] | null => {
  if (terms.length === 0) {
    return [newTerm];
  }

  if (cursorMatch) {
    const { index, term } = cursorMatch;
    const previousTerm = terms[index - 1];

    const isAtTermStart = term.startPosition === cursorPosition;
    const isAtTermEnd = term.endPosition === cursorPosition;

    if (isNotOperator(term) || isNotOperator(previousTerm)) {
      return terms;
    }

    if (isGroupStart(term) && isAtTermStart) {
      return insertTermAtIndex(terms, index, newTerm);
    }

    if (isGroupEnd(term) && isAtTermEnd) {
      return insertTermAtIndex(terms, index + 1, newTerm);
    }

    if (isCriterion(term)) {
      return insertTermAtIndex(terms, index, newTerm);
    }

    return insertTermAtIndex(terms, index + 1, newTerm);
  }

  const insertionIndex = getInsertionIndex(terms, cursorPosition);
  const previousTerm = terms[insertionIndex - 1];

  if (isNotOperator(previousTerm)) {
    return terms;
  }

  return insertTermAtIndex(terms, insertionIndex, newTerm);
};

const insertTerm = (
  terms: SearchQueryTerm[],
  cursorPosition: number,
  cursorMatch: CursorMatch | undefined,
  newTerm: SearchQueryTermWithoutPositions,
): SearchQueryTermWithoutPositions[] | null => {
  if (terms.length === 0) {
    return [newTerm];
  }

  if (cursorMatch) {
    const { index, term } = cursorMatch;

    const nextTerm = terms[index + 1];

    const isAtTermStart = term.startPosition === cursorPosition;
    const isAtTermEnd = term.endPosition === cursorPosition;

    if (isOperator(term) && isCriterion(newTerm)) {
      if (isCriterion(nextTerm)) {
        return replaceTermAtIndex(terms, index + 1, newTerm);
      }

      return insertTermAtIndex(terms, index + 1, newTerm);
    }

    if (isCriterion(term) && isOperator(newTerm)) {
      return insertTermAtIndex(terms, index + 1, newTerm);
    }

    if (isGroupStart(term)) {
      if (isAtTermStart) {
        return insertTermAtIndex(terms, index, newTerm);
      }

      return insertTermAtIndex(terms, index + 1, newTerm);
    }

    if (isGroupEnd(term)) {
      if (isAtTermEnd) {
        return insertTermAtIndex(terms, index + 1, newTerm);
      }

      return insertTermAtIndex(terms, index, newTerm);
    }

    return replaceTermAtIndex(terms, index, newTerm);
  }

  const insertionIndex = getInsertionIndex(terms, cursorPosition);
  const previousTerm = terms[insertionIndex - 1];

  if (areTermsSameType(previousTerm, newTerm)) {
    return null;
  }

  return insertTermAtIndex(terms, insertionIndex, newTerm);
};

export const insertTermAtCursorPosition = (
  rawQuery: string,
  cursorPosition: number,
  newTerm: SearchQueryTermWithoutPositions,
) => {
  const { query } = searchRegexLanguageParser.parse(rawQuery);
  const cursorMatch = findCursorMatch(query, cursorPosition, rawQuery);

  const updatedQuery = isNotOperator(newTerm)
    ? insertNotTerm(query, cursorPosition, cursorMatch, newTerm)
    : insertTerm(query, cursorPosition, cursorMatch, newTerm);

  if (!updatedQuery) {
    return rawQuery;
  }

  return searchRegexLanguageParser.serialize(updatedQuery);
};
